import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Container
} from '@mui/material';
import { API_BASE_URL } from '../services/api';


const initialForm = {
  bankName: '',
  bankPaymentCode: '',
  bankCode: '',
  address: {
    buildingNumber: '',
    streetName: '',
    streetName2: '',
    city: '',
    province: '',
    zipCode: '',
    country: '',
  },
  contact: {
    name: '',
    phone: '',
    mobile: '',
    fax: '',
    email: '',
  },
  accountType: '',
  bankAccountNumber: '',
  bankBranch: '',
};

const accountTypes = [
  'Savings',
  'Current',
  'Money Market',
  'Fixed deposit',
];
const countries = ['Sri Lanka', 'India', 'Other']; // Example countries
const provinces = ['Western', 'Central', 'Other']; // Example provinces

export default function SettlementAccountMasterPage() {
  const [form, setForm] = useState(initialForm);
  const [mode, setMode] = useState('add'); // add, search, modify
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");




  const [searchTerm, setSearchTerm] = useState("");
  const [bankList, setBankList] = useState([]);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    if (mode === 'search' || mode === 'modify') {
      fetchBanks(searchTerm);
    }
    // eslint-disable-next-line
  }, [mode]);

  const fetchBanks = async (q = "") => {
    try {
      const res = await fetch(`${API_BASE_URL}/settlement-accounts?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (data.success) setBankList(data.data);
      else setBankList([]);
    } catch (e) {
      setBankList([]);
    }
  };

  const handleEdit = (bank) => {
    setForm({
      bankName: bank.bank_name,
      bankPaymentCode: bank.bank_payment_code,
      bankCode: bank.bank_code,
      address: {
        buildingNumber: bank.address_building_number,
        streetName: bank.address_street_name,
        streetName2: bank.address_street_name2,
        city: bank.address_city,
        province: bank.address_province,
        zipCode: bank.address_zip_code,
        country: bank.address_country,
      },
      contact: {
        name: bank.contact_name,
        phone: bank.contact_phone,
        mobile: bank.contact_mobile,
        fax: bank.contact_fax,
        email: bank.contact_email,
      },
      accountType: bank.account_type,
      bankAccountNumber: bank.bank_account_number,
      bankBranch: bank.bank_branch,
    });
    setEditId(bank.id);
    setMode('modify');
    setSuccessMsg("");
    setErrorMsg("");
  };




  const handleChange = (e, section, field) => {
    if (section) {
      setForm({
        ...form,
        [section]: {
          ...form[section],
          [field]: e.target.value,
        },
      });
    } else {
      setForm({ ...form, [field]: e.target.value });
    }
  };





  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    if (mode === 'add') {
      try {
        const response = await fetch(`${API_BASE_URL}/settlement-accounts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to save bank');
        }
        setSuccessMsg('Bank saved successfully!');
        setForm(initialForm);
      } catch (err) {
        setErrorMsg(err.message || 'Error saving bank');
      }
    } else if (mode === 'modify' && editId) {
      try {
        const response = await fetch(`${API_BASE_URL}/settlement-accounts/${editId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to update bank');
        }
        setSuccessMsg('Bank updated successfully!');
        setForm(initialForm);
        setEditId(null);
        setMode('search');
        fetchBanks(searchTerm);
      } catch (err) {
        setErrorMsg(err.message || 'Error updating bank');
      }
    } else if (mode === 'search') {
      fetchBanks(searchTerm);
    } else {
      alert(`${mode.charAt(0).toUpperCase() + mode.slice(1)} Bank functionality coming soon!`);
    }
  };

  return (
    <Container maxWidth="md" className="ui-page admin-page-shell" sx={{ py: 4 }}>
      <Card className="ui-card" sx={{ p: 3, border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        <Typography variant="h4" gutterBottom>Settlement Account Master</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button variant={mode === 'add' ? 'contained' : 'outlined'} onClick={() => { setMode('add'); setForm(initialForm); setEditId(null); }}>Add Bank</Button>
          <Button variant={mode === 'search' ? 'contained' : 'outlined'} onClick={() => { setMode('search'); setForm(initialForm); setEditId(null); }}>Search Bank</Button>
        </Box>

        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

        {(mode === 'search' || mode === 'modify') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              label="Search by bank name"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ width: 250 }}
            />
            <Button variant="contained" onClick={() => fetchBanks(searchTerm)}>Search</Button>
          </Box>
        )}

        {(mode === 'search' || mode === 'modify') && bankList.length > 0 && (
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bank Name</TableCell>
                  <TableCell>Bank Code</TableCell>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankList.map(bank => (
                  <TableRow key={bank.id}>
                    <TableCell>{bank.bank_name}</TableCell>
                    <TableCell>{bank.bank_code}</TableCell>
                    <TableCell>{bank.bank_account_number}</TableCell>
                    <TableCell>
                      <Button color="primary" onClick={() => handleEdit(bank)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Bank Name"
                value={form.bankName}
                onChange={e => handleChange(e, null, 'bankName')}
                required
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Bank Code"
                value={form.bankCode}
                onChange={e => handleChange(e, null, 'bankCode')}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Bank Payment Code"
                value={form.bankPaymentCode}
                onChange={e => handleChange(e, null, 'bankPaymentCode')}
                required
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>

          <Card sx={{ my: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>Address</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Building Number"
                  value={form.address.buildingNumber}
                  onChange={e => handleChange(e, 'address', 'buildingNumber')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Street Name"
                  value={form.address.streetName}
                  onChange={e => handleChange(e, 'address', 'streetName')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Street Name 2"
                  value={form.address.streetName2}
                  onChange={e => handleChange(e, 'address', 'streetName2')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="City"
                  value={form.address.city}
                  onChange={e => handleChange(e, 'address', 'city')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Province"
                  value={form.address.province}
                  onChange={e => handleChange(e, 'address', 'province')}
                  fullWidth
                  margin="normal"
                >
                  <MenuItem value="">Select</MenuItem>
                  {provinces.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Zip Code"
                  value={form.address.zipCode}
                  onChange={e => handleChange(e, 'address', 'zipCode')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Country"
                  value={form.address.country}
                  onChange={e => handleChange(e, 'address', 'country')}
                  fullWidth
                  margin="normal"
                >
                  <MenuItem value="">Select</MenuItem>
                  {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ my: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>Contact Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Name"
                  value={form.contact.name}
                  onChange={e => handleChange(e, 'contact', 'name')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Phone"
                  value={form.contact.phone}
                  onChange={e => handleChange(e, 'contact', 'phone')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Mobile"
                  value={form.contact.mobile}
                  onChange={e => handleChange(e, 'contact', 'mobile')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Fax"
                  value={form.contact.fax}
                  onChange={e => handleChange(e, 'contact', 'fax')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={form.contact.email}
                  onChange={e => handleChange(e, 'contact', 'email')}
                  fullWidth
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Account Type"
                value={form.accountType}
                onChange={e => handleChange(e, null, 'accountType')}
                fullWidth
                margin="normal"
              >
                <MenuItem value="">Select</MenuItem>
                {accountTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Bank Account Number"
                value={form.bankAccountNumber}
                onChange={e => handleChange(e, null, 'bankAccountNumber')}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Bank Branch"
                value={form.bankBranch}
                onChange={e => handleChange(e, null, 'bankBranch')}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button type="submit" variant="contained">
              {mode === 'add' ? 'Add Bank' : mode === 'search' ? 'Search Bank' : 'Modify Bank'}
            </Button>
            {mode === 'modify' && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => { setMode('search'); setForm(initialForm); setEditId(null); }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </Card>
    </Container>
  );

}

